/* /account/data - download everything, delete the account, and the student's rights. */
import { useState, type FormEvent } from 'react';
import { WEB_ROUTES } from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { Link, navigate } from '../app/nav';
import { useSession } from '../app/session';
import { useErrorHandler } from '../app/hooks';
import { Icon } from '../app/icons';
import { Alert, Button, Live, PageHeader, Section, TextField } from '../app/ui';
import { api, download } from '../lib/api';
import { localTodayISO } from '../lib/forms';

export function Data() {
  const { t, rich } = useT();
  return (
    <>
      <PageHeader title={t('data.title')} lead={t('data.lead')} back={{ to: WEB_ROUTES.account, label: t('nav.account') }} />
      <Export />
      <Delete />
      <Section title={t('data.rightsTitle')} id="rights">
        <ul className="ticks">
          <li>{t('data.rightAccess')}</li>
          <li>{rich('data.rightCorrect', { profile: <Link to={WEB_ROUTES.profile}>{t('data.profileLink')}</Link> })}</li>
          <li>{t('data.rightDelete')}</li>
          <li>{t('data.rightWithdraw')}</li>
          <li>{t('data.rightObject')}</li>
          <li>{t('data.rightPort')}</li>
          <li>{t('data.rightComplain')} <Link to={`${WEB_ROUTES.privacy}#rights`}>{t('footer.privacy')}</Link></li>
        </ul>
        <p className="mt">{t('data.contact', { email: t('company.privacyEmail') })}</p>
      </Section>
    </>
  );
}

function Export() {
  const { t } = useT();
  const handle = useErrorHandler();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const run = async () => {
    if (busy) return;
    setBusy(true); setMsg(null);
    try {
      const file = await download('/api/account/export', `pathabroad-export-${localTodayISO()}.json`);
      const url = URL.createObjectURL(file.blob);
      const a = document.createElement('a');
      a.href = url; a.download = file.name; a.hidden = true;
      document.body.appendChild(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      setMsg({ tone: 'ok', text: t('data.exportDone') });
    } catch (e) {
      const m = handle(e);
      if (m) setMsg({ tone: 'error', text: m });
    } finally { setBusy(false); }
  };
  return (
    <Section title={t('data.exportTitle')} id="export">
      <div className="card stack">
        <p>{t('data.exportText')}</p>
        <div><Button busy={busy} onClick={() => void run()}><Icon.download />{t('data.exportTitle')}</Button></div>
        <Live>{msg && <span className={msg.tone === 'error' ? 'text-danger' : 'text-ok'}>{msg.text}</span>}</Live>
      </div>
    </Section>
  );
}

function Delete() {
  const { t } = useT();
  const { markOut, setFarewell } = useSession();
  const handle = useErrorHandler();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = typed.trim() === 'DELETE';
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !ready) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/account/delete', { confirm: 'DELETE' });
      /* the server has deleted the account and cleared the cookies: forget the session here and say goodbye */
      setFarewell(true);
      markOut();
      navigate('/', { replace: true });
    } catch (er) {
      setBusy(false);
      const m = handle(er);
      if (m) setError(m);
    }
  };
  return (
    <Section title={t('data.deleteTitle')} id="delete">
      <form className="card stack danger-zone" noValidate onSubmit={submit} aria-labelledby="delete-title">
        <div>
          <p>{t('data.deleteWhat')}</p>
          <ul className="bullets">
            <li>{t('data.delete1')}</li>
            <li>{t('data.delete2')}</li>
            <li>{t('data.delete3')}</li>
            <li>{t('data.delete4')}</li>
            <li>{t('data.delete5')}</li>
          </ul>
        </div>
        <p className="muted">{t('data.deleteKept')}</p>
        <p><strong>{t('data.deleteFinal')}</strong></p>
        <TextField id="delete-confirm" label={t('data.deleteType')} value={typed} onChange={setTyped} ltr autoComplete="off"
          autoCapitalize="characters" spellCheck={false} />
        {error && <Alert>{error}</Alert>}
        <div><Button type="submit" variant="danger-solid" busy={busy} disabled={!ready}>{t('data.deleteButton')}</Button></div>
      </form>
    </Section>
  );
}
